import { useRef, useCallback } from 'react';
import type { MapObject, MapObjectCategory } from '@roll1/shared';
import type { CanvasEngine } from './useCanvasEngine';
import { hitTestRegion, getRegionCenter, getRegionWidth, truncateLabel } from '../utils/geometry';

export interface UseMapObjectsOptions {
  engine: CanvasEngine;
  isGM: boolean;
  gridSizeFtRef?: React.MutableRefObject<number>;
}

export interface UseMapObjectsReturn {
  objects: React.MutableRefObject<MapObject[]>;
  hoveredObjectId: React.MutableRefObject<string | null>;
  selectedObjectId: React.MutableRefObject<string | null>;
  getObjectAtWorld: (wx: number, wy: number) => MapObject | null;
  setObjects: (newObjects: MapObject[]) => void;
  renderMapObjects: () => void;
}

/* ---- Category-based color map (RGB strings) ---- */
const CATEGORY_COLORS: Record<MapObjectCategory, string> = {
  furniture: '139, 92, 246',
  terrain: '34, 197, 94',
  hazard: '239, 68, 68',
  door: '234, 179, 8',
  container: '168, 85, 247',
  light: '250, 204, 21',
  custom: '148, 163, 184',
};

/** Parse hex color (#RRGGBB) to RGB string */
function hexToRgb(hex: string): string | null {
  const match = /^#([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/.exec(hex);
  if (!match) return null;
  return `${parseInt(match[1], 16)}, ${parseInt(match[2], 16)}, ${parseInt(match[3], 16)}`;
}

export function useMapObjects(options: UseMapObjectsOptions): UseMapObjectsReturn {
  const { engine, isGM, gridSizeFtRef } = options;
  const objects = useRef<MapObject[]>([]);
  const hoveredObjectId = useRef<string | null>(null);
  const selectedObjectId = useRef<string | null>(null);

  const setObjects = useCallback((newObjects: MapObject[]) => {
    objects.current = newObjects;
    engine.markAllDirty();
  }, [engine]);

  const getObjectAtWorld = useCallback((wx: number, wy: number): MapObject | null => {
    const arr = objects.current;
    for (let i = arr.length - 1; i >= 0; i--) {
      const obj = arr[i];
      if (!isGM && obj.visibility !== 'all') continue;
      if (hitTestRegion(wx, wy, obj.region)) {
        return obj;
      }
    }
    return null;
  }, [isGM]);

  const renderMapObjects = useCallback(() => {
    const canvas = engine.refs.current.ui;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const cam = engine.cameraRef.current;
    const arr = objects.current;

    for (const obj of arr) {
      const isGmOnly = obj.visibility === 'gm';
      if (!isGM && isGmOnly) continue;

      const isHovered = hoveredObjectId.current === obj.id;
      const isSelected = selectedObjectId.current === obj.id;

      // Determine color
      const rgb = (obj.style.color ? hexToRgb(obj.style.color) : null) ?? CATEGORY_COLORS[obj.category] ?? CATEGORY_COLORS.custom;

      const opacityMod = obj.style.opacity ?? 1;
      const gmOnlyMod = isGmOnly ? 0.5 : 1;
      const baseMod = opacityMod * gmOnlyMod;

      let fillAlpha = (isHovered ? 0.3 : 0.15) * baseMod;
      let strokeAlpha = (isHovered ? 0.9 : 0.6) * baseMod;

      ctx.save();

      // Draw shape
      const region = obj.region;
      ctx.beginPath();

      switch (region.type) {
        case 'rect': {
          const sx = region.x * cam.zoom + cam.offsetX;
          const sy = region.y * cam.zoom + cam.offsetY;
          const sw = region.width * cam.zoom;
          const sh = region.height * cam.zoom;
          ctx.rect(sx, sy, sw, sh);
          break;
        }
        case 'circle': {
          const sx = region.cx * cam.zoom + cam.offsetX;
          const sy = region.cy * cam.zoom + cam.offsetY;
          const sr = region.radius * cam.zoom;
          ctx.arc(sx, sy, sr, 0, Math.PI * 2);
          break;
        }
        case 'polygon': {
          const pts = region.points;
          if (pts.length < 3) { ctx.restore(); continue; }
          ctx.moveTo(pts[0].x * cam.zoom + cam.offsetX, pts[0].y * cam.zoom + cam.offsetY);
          for (let i = 1; i < pts.length; i++) {
            ctx.lineTo(pts[i].x * cam.zoom + cam.offsetX, pts[i].y * cam.zoom + cam.offsetY);
          }
          ctx.closePath();
          break;
        }
      }

      // Fill
      ctx.fillStyle = `rgba(${rgb}, ${fillAlpha})`;
      ctx.fill();

      // Stroke
      ctx.strokeStyle = `rgba(${rgb}, ${strokeAlpha})`;
      ctx.lineWidth = isSelected ? 3 : 2;
      if (isGmOnly) ctx.setLineDash([6, 4]);
      ctx.stroke();
      ctx.setLineDash([]);

      // Label
      if (obj.label) {
        const center = getRegionCenter(region);
        const scx = center.x * cam.zoom + cam.offsetX;
        const scy = center.y * cam.zoom + cam.offsetY;
        const fontSize = Math.max(10, Math.round(12 * cam.zoom));
        ctx.font = `${fontSize}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = `rgba(255, 255, 255, 0.9)`;

        let labelY = scy;
        // Icon before label
        if (obj.style.icon) {
          const iconSize = Math.max(12, Math.round(14 * cam.zoom));
          ctx.font = `${iconSize}px sans-serif`;
          ctx.fillText(obj.style.icon, scx, scy - Math.round(fontSize * 0.7));
          labelY = scy + Math.round(fontSize * 0.5);
          ctx.font = `${fontSize}px sans-serif`;
        }

        const maxWidth = getRegionWidth(region) * cam.zoom * 0.9;
        const displayLabel = truncateLabel(ctx, obj.label, maxWidth);
        ctx.fillText(displayLabel, scx, labelY);
      }

      // GM light radius indicator for light-category objects
      if (isGM && obj.category === 'light' && obj.properties.state !== 'extinguished') {
        const lightProps = obj.properties.light;
        if (lightProps && lightProps.radius > 0) {
          const center = getRegionCenter(region);
          const scx = center.x * cam.zoom + cam.offsetX;
          const scy = center.y * cam.zoom + cam.offsetY;
          const gridSizePx = engine.gridSizeRef.current;
          const gsFt = gridSizeFtRef?.current ?? 5;
          const ftToPx = gridSizePx / gsFt;
          const brightRadiusPx = lightProps.radius * ftToPx * cam.zoom;
          const dimRadiusPx = lightProps.radius * 2 * ftToPx * cam.zoom;

          // Dim radius (outer dashed circle)
          ctx.beginPath();
          ctx.arc(scx, scy, dimRadiusPx, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(${rgb}, 0.2)`;
          ctx.lineWidth = 1;
          ctx.setLineDash([4, 4]);
          ctx.stroke();
          ctx.setLineDash([]);

          // Bright radius (inner solid circle)
          ctx.beginPath();
          ctx.arc(scx, scy, brightRadiusPx, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(${rgb}, 0.4)`;
          ctx.lineWidth = 1.5;
          ctx.stroke();
        }
      }

      // TODO: Phase D — resize handles (hit-test + drag logic for rect/circle/polygon resizing)

      ctx.restore();
    }
  }, [engine, isGM, gridSizeFtRef]);

  return {
    objects,
    hoveredObjectId,
    selectedObjectId,
    getObjectAtWorld,
    setObjects,
    renderMapObjects,
  };
}
